import { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, X, ChevronLeft, ChevronRight, Loader2 } from "lucide-react";
import axios from "axios";
import SEO from "@/components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cloudinary optimization constants
const THUMBNAIL_TRANSFORM = "f_auto,q_auto,w_1200,dpr_auto";

// Two-stage lightbox loading (no dpr_auto for controlled file sizes)
const LIGHTBOX_FAST = "f_auto,q_auto,w_1200";      // Instant preview
const LIGHTBOX_SHARP = "f_auto,q_auto,w_1800";     // Sharp default
const LIGHTBOX_SHARP_LARGE = "f_auto,q_auto,w_2000"; // Sharp for large viewports

const INITIAL_LOAD_COUNT = 36;
const LOAD_MORE_COUNT = 18;

/**
 * Get sharp lightbox transform based on viewport width
 */
const getSharpTransform = () => {
  if (typeof window !== 'undefined' && window.innerWidth >= 1800) {
    return LIGHTBOX_SHARP_LARGE;
  }
  return LIGHTBOX_SHARP;
};

/**
 * Generate optimized Cloudinary URL with transformations
 * Inserts transform string after /image/upload/
 */
const getOptimizedUrl = (originalUrl, transform) => {
  if (!originalUrl) return originalUrl;
  
  // Check if it's a Cloudinary URL
  const uploadIndex = originalUrl.indexOf("/image/upload/");
  if (uploadIndex === -1) {
    // Not a Cloudinary URL, return as-is
    return originalUrl;
  }
  
  // Insert transformation after /image/upload/
  const beforeUpload = originalUrl.substring(0, uploadIndex + 14); // includes "/image/upload/"
  const afterUpload = originalUrl.substring(uploadIndex + 14);
  
  return `${beforeUpload}${transform}/${afterUpload}`;
};

/**
 * Preload an image URL and return a promise
 */
const preloadImage = (url) => {
  if (!url) return Promise.resolve();
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = resolve;
    img.onerror = resolve;
    img.src = url;
  });
};

/**
 * Preload image in background (fire and forget)
 */
const preloadImageAsync = (url) => {
  if (!url) return;
  const img = new Image();
  img.src = url;
};

/**
 * Premium Gallery Page
 * - Editorial grid with large images
 * - Staggered fade-in animation
 * - Desktop: subtle zoom + shadow on hover
 * - Mobile: gentle press animation
 * - Optimized Cloudinary thumbnails
 * - Infinite scroll with load-more
 */
const GalleryPage = () => {
  const [allImages, setAllImages] = useState([]); // All images from API
  const [displayedCount, setDisplayedCount] = useState(INITIAL_LOAD_COUNT); // How many to display
  const [loading, setLoading] = useState(true);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [prevLightboxIndex, setPrevLightboxIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [fastImageLoaded, setFastImageLoaded] = useState(false);
  const [sharpImageReady, setSharpImageReady] = useState(false);
  const [isUpgrading, setIsUpgrading] = useState(false); // Cross-fade from preview to sharp
  const [currentFastUrl, setCurrentFastUrl] = useState("");
  const [currentSharpUrl, setCurrentSharpUrl] = useState("");
  const [visibleImages, setVisibleImages] = useState(new Set());
  const observerRef = useRef(null);
  const loadMoreRef = useRef(null);
  const loadMoreObserverRef = useRef(null);
  const sharpPreloadTimeoutRef = useRef(null);
  const sharpTransformRef = useRef(getSharpTransform());
  
  // Token system to prevent race conditions
  const requestIdRef = useRef(0);
  const activeRequestIdRef = useRef(0);

  // Computed: images currently displayed
  const images = allImages.slice(0, displayedCount);
  const hasMore = displayedCount < allImages.length;

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const res = await axios.get(`${API}/galleries/main`);
        setAllImages(res.data || []);
      } catch (err) {
        console.error("Error fetching gallery:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  // Load more images when scrolling near bottom
  const loadMore = useCallback(() => {
    if (hasMore) {
      setDisplayedCount(prev => Math.min(prev + LOAD_MORE_COUNT, allImages.length));
    }
  }, [hasMore, allImages.length]);

  // Intersection observer for load-more trigger
  useEffect(() => {
    if (loading || !hasMore) return;

    loadMoreObserverRef.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore();
        }
      },
      { threshold: 0.1, rootMargin: "200px" }
    );

    if (loadMoreRef.current) {
      loadMoreObserverRef.current.observe(loadMoreRef.current);
    }

    return () => {
      if (loadMoreObserverRef.current) {
        loadMoreObserverRef.current.disconnect();
      }
    };
  }, [loading, hasMore, loadMore]);

  // Intersection observer for staggered fade-in
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.index, 10);
            // Stagger the animation based on position in viewport batch
            setTimeout(() => {
              setVisibleImages((prev) => new Set([...prev, index]));
            }, (index % 6) * 80); // Stagger within batches of 6
            observerRef.current.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1, rootMargin: "50px" }
    );

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // Lightbox keyboard navigation
  useEffect(() => {
    if (!lightboxOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        closeLightbox();
      } else if (e.key === "ArrowLeft") {
        navigateLightbox(-1);
      } else if (e.key === "ArrowRight") {
        navigateLightbox(1);
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    document.body.style.overflow = "hidden";

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = "";
    };
  }, [lightboxOpen, lightboxIndex]);

  const openLightbox = (index) => {
    // Generate new request token
    requestIdRef.current += 1;
    const thisRequestId = requestIdRef.current;
    activeRequestIdRef.current = thisRequestId;
    
    // Update sharp transform based on current viewport
    sharpTransformRef.current = getSharpTransform();
    
    // Reset states
    setFastImageLoaded(false);
    setSharpImageReady(false);
    setIsUpgrading(false);
    setCurrentFastUrl("");
    setCurrentSharpUrl("");
    setLightboxIndex(index);
    setPrevLightboxIndex(index);
    setLightboxOpen(true);
    
    // Set fast URL for immediate display
    const fastUrl = getOptimizedUrl(allImages[index]?.url, LIGHTBOX_FAST);
    setCurrentFastUrl(fastUrl);
    
    // Preload adjacent images in FAST preview form
    if (allImages.length > 1) {
      const prevIndex = index === 0 ? allImages.length - 1 : index - 1;
      const nextIndex = index === allImages.length - 1 ? 0 : index + 1;
      preloadImageAsync(getOptimizedUrl(allImages[prevIndex]?.url, LIGHTBOX_FAST));
      preloadImageAsync(getOptimizedUrl(allImages[nextIndex]?.url, LIGHTBOX_FAST));
    }
    
    // Load sharp version in background
    const sharpUrl = getOptimizedUrl(allImages[index]?.url, sharpTransformRef.current);
    preloadImage(sharpUrl).then(() => {
      // Only apply if this is still the active request
      if (activeRequestIdRef.current === thisRequestId) {
        setCurrentSharpUrl(sharpUrl);
        // Cross-fade is handled by the onLoad event on the sharp image element
      }
    });
    
    // After idle, preload next image in sharp form
    clearTimeout(sharpPreloadTimeoutRef.current);
    sharpPreloadTimeoutRef.current = setTimeout(() => {
      if (allImages.length > 1 && activeRequestIdRef.current === thisRequestId) {
        const nextIndex = index === allImages.length - 1 ? 0 : index + 1;
        preloadImageAsync(getOptimizedUrl(allImages[nextIndex]?.url, sharpTransformRef.current));
      }
    }, 400);
  };

  const closeLightbox = () => {
    // Invalidate any pending requests
    requestIdRef.current += 1;
    activeRequestIdRef.current = requestIdRef.current;
    
    setLightboxOpen(false);
    setFastImageLoaded(false);
    setSharpImageReady(false);
    setIsUpgrading(false);
    setCurrentFastUrl("");
    setCurrentSharpUrl("");
    clearTimeout(sharpPreloadTimeoutRef.current);
  };

  const navigateLightbox = (direction) => {
    if (isTransitioning) return;
    
    const newIndex = (() => {
      const next = lightboxIndex + direction;
      if (next < 0) return allImages.length - 1;
      if (next >= allImages.length) return 0;
      return next;
    })();
    
    // Generate new request token - invalidates all pending loads
    requestIdRef.current += 1;
    const thisRequestId = requestIdRef.current;
    activeRequestIdRef.current = thisRequestId;
    
    // Clear pending preloads
    clearTimeout(sharpPreloadTimeoutRef.current);
    
    // Start crossfade transition
    setPrevLightboxIndex(lightboxIndex);
    setIsTransitioning(true);
    setFastImageLoaded(false);
    setSharpImageReady(false);
    setIsUpgrading(false);
    setCurrentSharpUrl("");
    setLightboxIndex(newIndex);
    
    // Set fast URL for new image
    const fastUrl = getOptimizedUrl(allImages[newIndex]?.url, LIGHTBOX_FAST);
    setCurrentFastUrl(fastUrl);
    
    // Preload next adjacent image in fast form
    const preloadIndex = direction > 0 
      ? (newIndex === allImages.length - 1 ? 0 : newIndex + 1)
      : (newIndex === 0 ? allImages.length - 1 : newIndex - 1);
    preloadImageAsync(getOptimizedUrl(allImages[preloadIndex]?.url, LIGHTBOX_FAST));
    
    // Load sharp version for new image
    const sharpUrl = getOptimizedUrl(allImages[newIndex]?.url, sharpTransformRef.current);
    preloadImage(sharpUrl).then(() => {
      // Only apply if this is still the active request
      if (activeRequestIdRef.current === thisRequestId) {
        setCurrentSharpUrl(sharpUrl);
        // Cross-fade is handled by the onLoad event on the sharp image element
      }
    });
    
    // End navigation transition after animation completes
    setTimeout(() => {
      setIsTransitioning(false);
    }, 350);
    
    // After idle, preload next in sharp form
    sharpPreloadTimeoutRef.current = setTimeout(() => {
      if (activeRequestIdRef.current === thisRequestId) {
        const nextSharpIndex = direction > 0 
          ? (newIndex === allImages.length - 1 ? 0 : newIndex + 1)
          : (newIndex === 0 ? allImages.length - 1 : newIndex - 1);
        preloadImageAsync(getOptimizedUrl(allImages[nextSharpIndex]?.url, sharpTransformRef.current));
      }
    }, 400);
  };

  // Touch swipe for lightbox
  const touchStartX = useRef(0);
  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
  };
  const handleTouchEnd = (e) => {
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      navigateLightbox(diff > 0 ? 1 : -1);
    }
  };

  return (
    <div className="min-h-screen bg-white pt-20">
      <SEO 
        title="Galleri | Birk Anlæg"
        description="Se eksempler på vores anlægsprojekter i København og omegn. Flisebelægning, natursten, rullegræs, hegn og mere. Kvalitet og håndværk i fokus."
        canonical="/galleri"
      />
      {/* Hero Section */}
      <section className="bg-[#1A3C34] py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1
            className="text-4xl md:text-6xl font-bold text-white mb-6"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Galleri
          </h1>
          <p className="text-xl text-gray-300 max-w-2xl mx-auto">
            Et udvalg af vores projekter. Kvalitet og håndværk i fokus.
          </p>
        </div>
      </section>

      {/* Gallery Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
              {[...Array(4)].map((_, i) => (
                <div
                  key={i}
                  className="aspect-[4/3] bg-gray-100 animate-pulse rounded-lg"
                />
              ))}
            </div>
          ) : images.length > 0 ? (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                {images.map((image, index) => (
                  <GalleryImage
                    key={image.id || index}
                    image={image}
                    index={index}
                    isVisible={visibleImages.has(index)}
                    onClick={() => openLightbox(index)}
                    observerRef={observerRef}
                    thumbnailTransform={THUMBNAIL_TRANSFORM}
                  />
                ))}
              </div>
              
              {/* Load more trigger - invisible sentinel element */}
              {hasMore && (
                <div 
                  ref={loadMoreRef}
                  className="h-20 flex items-center justify-center mt-8"
                >
                  <div className="flex gap-2">
                    <div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: "0ms" }} />
                    <div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: "150ms" }} />
                    <div className="w-2 h-2 bg-gray-300 rounded-full animate-bounce" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="text-center py-20">
              <p className="text-gray-500 text-lg">
                Galleriet er under opbygning.
              </p>
              <p className="text-gray-400 mt-2">
                Kom tilbage snart for at se vores projekter.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-28 bg-[#1A3C34]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2
            className="text-3xl md:text-4xl font-bold text-white mb-6"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Klar til dit eget projekt?
          </h2>
          <p className="text-lg text-gray-300 mb-10 max-w-2xl mx-auto">
            Kontakt os for en uforpligtende snak om dine ønsker og muligheder.
          </p>
          <Link
            to="/kontakt"
            data-testid="gallery-cta"
            data-cta="primary"
            data-cta-variant="white"
            className="inline-flex items-center gap-2 bg-white text-[#1A3C34] rounded-full px-10 py-4 text-lg font-medium"
          >
            Kontakt os <ArrowRight size={20} />
          </Link>
        </div>
      </section>

      {/* Lightbox */}
      {lightboxOpen && allImages.length > 0 && (
        <div
          className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center"
          onClick={closeLightbox}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 z-10 p-2 text-white/70 hover:text-white transition-colors"
            aria-label="Luk"
          >
            <X size={28} />
          </button>

          {/* Navigation buttons */}
          {allImages.length > 1 && (
            <>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigateLightbox(-1);
                }}
                className="absolute left-2 md:left-6 z-10 p-2 md:p-3 text-white/70 hover:text-white transition-colors bg-black/30 rounded-full"
                aria-label="Forrige"
              >
                <ChevronLeft size={28} />
              </button>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  navigateLightbox(1);
                }}
                className="absolute right-2 md:right-6 z-10 p-2 md:p-3 text-white/70 hover:text-white transition-colors bg-black/30 rounded-full"
                aria-label="Næste"
              >
                <ChevronRight size={28} />
              </button>
            </>
          )}

          {/* Image - Two-stage loading container */}
          <div
            className="relative max-w-[90vw] max-h-[85vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Loading spinner - only if fast preview not ready */}
            {!fastImageLoaded && !isTransitioning && (
              <div className="absolute inset-0 flex items-center justify-center z-20">
                <Loader2 className="w-8 h-8 text-white/50 animate-spin" />
              </div>
            )}
            
            {/* Previous image (fades out during transition) */}
            {isTransitioning && prevLightboxIndex !== lightboxIndex && (
              <img
                key={`prev-${prevLightboxIndex}`}
                src={getOptimizedUrl(allImages[prevLightboxIndex]?.url, LIGHTBOX_FAST)}
                alt=""
                className="absolute max-w-full max-h-[85vh] object-contain opacity-100 animate-fadeOut"
                style={{ animationDuration: "300ms" }}
              />
            )}
            
            {/* Current image - Two-stage: fast preview with sharp overlay */}
            {/* Layer 1: Fast preview (always visible when loaded) */}
            <img
              key={`fast-${lightboxIndex}`}
              src={currentFastUrl || getOptimizedUrl(allImages[lightboxIndex]?.url, LIGHTBOX_FAST)}
              alt={allImages[lightboxIndex]?.alt || ""}
              className={`max-w-full max-h-[85vh] object-contain ${
                isTransitioning ? "animate-fadeIn" : fastImageLoaded ? "opacity-100" : "opacity-0"
              }`}
              style={{ animationDuration: "300ms" }}
              onLoad={() => setFastImageLoaded(true)}
            />
            
            {/* Layer 2: Sharp image overlay (cross-fades in over the preview) */}
            {currentSharpUrl && (
              <img
                key={`sharp-${lightboxIndex}`}
                src={currentSharpUrl}
                alt=""
                className={`absolute max-w-full max-h-[85vh] object-contain transition-opacity duration-500 ease-out ${
                  sharpImageReady ? "opacity-100" : isUpgrading ? "opacity-0" : "opacity-0"
                }`}
                style={{ 
                  top: "50%", 
                  left: "50%", 
                  transform: "translate(-50%, -50%)" 
                }}
                onLoad={() => {
                  // Only trigger upgrade animation if still valid
                  setIsUpgrading(true);
                  setTimeout(() => {
                    setSharpImageReady(true);
                    setIsUpgrading(false);
                  }, 50); // Small delay to ensure transition triggers
                }}
              />
            )}
          </div>
          
          {/* Crossfade animation styles */}
          <style>{`
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            @keyframes fadeOut {
              from { opacity: 1; }
              to { opacity: 0; }
            }
            .animate-fadeIn {
              animation: fadeIn 300ms ease-out forwards;
            }
            .animate-fadeOut {
              animation: fadeOut 300ms ease-out forwards;
            }
          `}</style>

          {/* Caption */}
          {allImages[lightboxIndex]?.caption && (
            <div className="absolute bottom-6 left-0 right-0 text-center">
              <p className="text-white/80 text-sm md:text-base px-4">
                {allImages[lightboxIndex].caption}
              </p>
            </div>
          )}

          {/* Counter */}
          <div className="absolute bottom-6 right-6 text-white/50 text-sm">
            {lightboxIndex + 1} / {allImages.length}
          </div>
        </div>
      )}
    </div>
  );
};

/**
 * Individual Gallery Image with animations and optimized Cloudinary URLs
 */
const GalleryImage = ({ image, index, isVisible, onClick, observerRef, thumbnailTransform }) => {
  const imageRef = useRef(null);

  useEffect(() => {
    if (imageRef.current && observerRef.current) {
      observerRef.current.observe(imageRef.current);
    }
  }, [observerRef]);

  // Generate optimized thumbnail URL
  const thumbnailUrl = getOptimizedUrl(image.url, thumbnailTransform);

  return (
    <div
      ref={imageRef}
      data-index={index}
      onClick={onClick}
      className={`
        group relative overflow-hidden rounded-lg cursor-pointer
        transition-all duration-500 ease-out
        ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}
      `}
    >
      {/* Image container with aspect ratio */}
      <div className="aspect-[4/3] bg-gray-100 overflow-hidden">
        <img
          src={thumbnailUrl}
          alt={image.alt || ""}
          loading="lazy"
          className="
            w-full h-full object-cover
            transition-transform duration-500 ease-out
            md:group-hover:scale-[1.03]
          "
        />
      </div>

      {/* Hover overlay - desktop only */}
      <div className="
        absolute inset-0 bg-black/0
        transition-all duration-300
        md:group-hover:bg-black/10
        md:group-hover:shadow-xl
      " />

      {/* Caption overlay */}
      {image.caption && (
        <div className="
          absolute bottom-0 left-0 right-0
          bg-gradient-to-t from-black/60 to-transparent
          p-4 pt-12
          opacity-0 translate-y-2
          transition-all duration-300
          md:group-hover:opacity-100 md:group-hover:translate-y-0
        ">
          <p className="text-white text-sm md:text-base">
            {image.caption}
          </p>
        </div>
      )}

      {/* Mobile press feedback via CSS */}
      <style jsx>{`
        @media (hover: none) and (pointer: coarse) {
          div:active {
            transform: scale(0.98);
          }
        }
      `}</style>
    </div>
  );
};

export default GalleryPage;
